import { useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import { Glyph } from "@/components/ui/Glyph";
import {
  setSingleKeys,
  shortcutGroups,
  useSingleKeys,
  type Shortcut,
  type ShortcutGroup,
} from "./shortcuts";
import { useModal } from "./useModal";
import styles from "./shortcuts.module.css";

/** One shortcut's keys: "Ctrl K or ⌘ K", or "G then H". */
export function Keys({ shortcut }: { shortcut: Shortcut }) {
  const joiner = shortcut.sequence ? "then" : "or";
  return (
    <span className={styles.keys}>
      {shortcut.keys.map((chord, i) => (
        <span key={`${chord.join("+")}-${String(i)}`} className={styles.chord}>
          {i > 0 ? <span className={styles.joiner}>{joiner}</span> : null}
          {chord.map((k) => (
            <kbd key={k} className={styles.kbd}>
              {k}
            </kbd>
          ))}
        </span>
      ))}
    </span>
  );
}

/** A group of shortcuts as rows on hairlines: the keys, then what they do. */
export function ShortcutRows({ group, singleKeys }: { group: ShortcutGroup; singleKeys: boolean }) {
  return (
    <dl className={styles.rows}>
      {group.items.map((s, i) => {
        const off = Boolean(s.singleKey) && !singleKeys;
        return (
          <div key={`${s.what}-${String(i)}`} className={`${styles.row} ${off ? styles.off : ""}`}>
            <dt>
              <Keys shortcut={s} />
            </dt>
            <dd>
              {s.what}
              {off ? <span className={styles.offNote}>Turned off</span> : null}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/** The switch for single-key shortcuts, as WCAG 2.1.4 asks. */
export function SingleKeySwitch() {
  const on = useSingleKeys();
  return (
    <div className={styles.switchRow}>
      <span className={styles.switchText}>
        <span className={styles.switchTitle} id="zh-single-keys">
          Single-key shortcuts
        </span>
        <span className={styles.switchHelp}>
          Turn these off if you use speech input or press keys by accident. Ctrl K always works.
        </span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-labelledby="zh-single-keys"
        className={styles.switch}
        onClick={() => setSingleKeys(!on)}
      >
        <span className={styles.knob} />
      </button>
    </div>
  );
}

/** "?": every shortcut, with the ones for the screen you're on first. */
export function ShortcutsOverlay({ onClose }: { onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  useModal(box, onClose, close);
  const { pathname } = useLocation();
  const singleKeys = useSingleKeys();
  const groups = shortcutGroups(pathname);

  return createPortal(
    <div className={styles.scrim} onMouseDown={onClose}>
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby="zh-keys-title"
        className={`zh-glass ${styles.panel}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.head}>
          <h2 id="zh-keys-title" className={styles.title}>
            Keyboard shortcuts
          </h2>
          <button
            ref={close}
            type="button"
            className={styles.close}
            aria-label="Close"
            onClick={onClose}
          >
            <Glyph name="x" size={17} strokeWidth={2.2} />
          </button>
        </div>
        <div className={styles.body}>
          <div className={styles.groups}>
            {groups.map((g, i) => (
              <section
                key={g.title}
                className={`${styles.group} ${i === 0 && g.title.startsWith("On this screen") ? styles.here : ""}`}
                aria-labelledby={`zh-keys-${String(i)}`}
              >
                <h3 id={`zh-keys-${String(i)}`} className={styles.groupTitle}>
                  {g.title}
                </h3>
                <ShortcutRows group={g} singleKeys={singleKeys} />
              </section>
            ))}
          </div>
          <SingleKeySwitch />
        </div>
      </div>
    </div>,
    document.body,
  );
}
